import React, { useState } from 'react';
import { X, Download, Share } from 'lucide-react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { PWAInstallModal } from './PWAInstallModal';

interface PWAInstallBannerProps {
  organizationName?: string;
  organizationLogoUrl?: string;
}

export function PWAInstallBanner({ organizationName, organizationLogoUrl }: PWAInstallBannerProps) {
  const { isIOS, isInstalled, promptInstall, dismissPrompt, shouldShowBanner, canInstall } = usePWAInstall();
  const [showIOSModal, setShowIOSModal] = useState(false);

  if (isInstalled || !canInstall || !shouldShowBanner) return null;

  const handleInstallClick = async () => {
    if (isIOS) { setShowIOSModal(true); }
    else {
      const success = await promptInstall();
      if (success) dismissPrompt();
    }
  };

  const displayName = organizationName || 'this app';

  return (
    <>
      <div className="fixed bottom-[calc(3.75rem+env(safe-area-inset-bottom))] md:bottom-0 left-2 right-2 md:left-0 md:right-0 z-50 bg-card border border-border md:border-t md:border-x-0 md:border-b-0 rounded-xl md:rounded-none shadow-lg md:safe-area-bottom animate-slide-up">
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-theme-primary to-theme-secondary flex items-center justify-center shadow-md overflow-hidden">
            {organizationLogoUrl ? (
              <img src={organizationLogoUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <Download className="w-6 h-6 text-primary-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">Install {displayName}</p>
            <p className="text-xs text-muted-foreground">
              {isIOS ? (<>Tap <Share className="inline w-3 h-3 mx-0.5" /> then "Add to Home Screen"</>) : 'Add to home screen for quick access'}
            </p>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2">
            <button onClick={handleInstallClick} className="px-4 py-2 bg-gradient-to-r from-theme-primary to-theme-secondary text-primary-foreground text-sm font-medium rounded-lg hover:opacity-90 transition-opacity">
              {isIOS ? 'Show Me' : 'Install'}
            </button>
            <button onClick={() => dismissPrompt(7)} className="p-2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Dismiss">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      <PWAInstallModal isOpen={showIOSModal} onClose={() => setShowIOSModal(false)} organizationName={organizationName} />
    </>
  );
}
